<template>
  <Content class="customer-detail">
    <Card>
      <div slot="title">
        <Button type="text" icon="ios-arrow-back" @click="goBack">返回</Button>
        <span style="margin-left: 10px;">用户详情</span>
      </div>

      <Spin v-if="loading" fix></Spin>

      <div v-else-if="userInfo">
        <!-- 用户基本信息 -->
        <Card title="基本信息" style="margin-bottom: 16px;">
          <Row :gutter="24">
            <Col :xs="24" :sm="4" :md="3">
              <div class="user-avatar">
                <Avatar
                  :src="userInfo.avatarUrl"
                  size="88"
                  icon="ios-person"
                />
              </div>
            </Col>
            <Col :xs="24" :sm="10" :md="10">
              <div class="info-item">
                <span class="label">用户昵称：</span>
                <span class="value">{{ userInfo.nickName || userInfo.nickname || '-' }}</span>
              </div>
              <div class="info-item">
                <span class="label">手机号码：</span>
                <span class="value">{{ userInfo.phone || '-' }}</span>
              </div>
              <div class="info-item">
                <span class="label">用户ID：</span>
                <span class="value id-text">{{ userInfo._id ? userInfo._id.toString() : '-' }}</span>
              </div>
              <div class="info-item">
                <span class="label">注册时间：</span>
                <span class="value">{{ formatTime(userInfo.create_time) }}</span>
              </div>
              <div class="info-item">
                <span class="label">是否会员：</span>
                <span class="value">{{ userInfo.is_member !== false ? '是（登录即会员）' : '否' }}</span>
              </div>
              <div class="info-item">
                <span class="label">用户状态：</span>
                <Tag :color="getUserStatusColor(userInfo.status)">
                  {{ getUserStatusText(userInfo.status) }}
                </Tag>
              </div>
              <div class="info-item">
                <span class="label">用户标签：</span>
                <Tag v-for="tag in userInfo.tags" :key="tag" color="primary">{{ tag }}</Tag>
                <span v-if="!userInfo.tags || userInfo.tags.length === 0" class="value">-</span>
              </div>
            </Col>
            <Col :xs="24" :sm="10" :md="11">
              <div class="info-item">
                <span class="label">储值余额：</span>
                <span class="value value--inline">
                  ¥{{ ((userInfo.balance_fen || 0) / 100).toFixed(2) }}
                  <Button size="small" type="primary" ghost class="adjust-btn" @click="openAdjust">调账</Button>
                </span>
              </div>
              <div class="info-item">
                <span class="label">积分：</span>
                <span class="value">{{ userInfo.points || 0 }}</span>
              </div>
              <div class="info-item">
                <span class="label">权益卡：</span>
                <span class="value">{{ benefitCardSummary }}</span>
              </div>
              <div class="info-item">
                <span class="label">最后登录：</span>
                <span class="value">{{ formatTime(displayLastLoginTime) }}</span>
              </div>
              <div class="info-item">
                <span class="label">累计登录：</span>
                <span class="value">{{ displayLoginCount }} 次</span>
              </div>
              <div class="info-item merchant-row">
                <span class="label">店员权限：</span>
                <span class="value row-inline">
                  <i-switch
                    :key="(userInfo && userInfo._id) || 'u'"
                    v-model="merchantSwitch"
                    :disabled="merchantSaving"
                    @on-change="handleMerchantSwitch"
                  />
                  <span class="hint">开启后小程序「我的」显示工作台、订单中心；需重新登录生效</span>
                </span>
              </div>
            </Col>
          </Row>
        </Card>

        <!-- 权益卡 -->
        <Card title="权益卡" style="margin-bottom: 16px;">
          <Table v-if="benefitCards.length" :columns="benefitCardColumns" :data="benefitCards" border stripe size="small" />
          <div v-else class="empty-hint">该用户暂未领取权益卡</div>
        </Card>

        <!-- 消费统计 -->
        <Card title="消费统计" style="margin-bottom: 16px;">
          <Row :gutter="16" class="stats-cards">
            <Col span="6">
              <div class="stat-card">
                <div class="stat-value">{{ stats.totalOrders || 0 }}</div>
                <div class="stat-label">总订单数</div>
              </div>
            </Col>
            <Col span="6">
              <div class="stat-card">
                <div class="stat-value">¥{{ (stats.totalSpent || 0).toFixed(2) }}</div>
                <div class="stat-label">累计消费</div>
              </div>
            </Col>
            <Col span="6">
              <div class="stat-card">
                <div class="stat-value">¥{{ (stats.avgOrderAmount || 0).toFixed(2) }}</div>
                <div class="stat-label">平均订单金额</div>
              </div>
            </Col>
            <Col span="6">
              <div class="stat-card">
                <div class="stat-value">{{ stats.completedOrders || 0 }}</div>
                <div class="stat-label">完成订单</div>
              </div>
            </Col>
          </Row>
        </Card>

        <!-- 储值流水 -->
        <Card title="储值流水" style="margin-bottom: 16px;">
          <Table :columns="ledgerColumns" :data="ledgers" border stripe size="small" />
          <Page
            v-show="ledgerTotal > 0"
            :total="ledgerTotal"
            :current="ledgerPage"
            :page-size="10"
            show-total
            @on-change="loadLedgers"
            style="margin-top: 16px; text-align: right;"
          />
        </Card>

        <!-- 订单历史 -->
        <Card title="订单历史" style="margin-bottom: 16px;">
          <Table :columns="orderColumns" :data="orders" border stripe>
            <template slot-scope="{ row }" slot="status">
              <Tag :color="getStatusColor(row.command)">{{ getStatusName(row.command) }}</Tag>
            </template>
            <template slot-scope="{ row }" slot="action">
              <Button type="text" size="small" @click="viewOrder(row._id)">查看</Button>
            </template>
          </Table>
          <Page 
            v-show="orderTotal > 0"
            :total="orderTotal"
            :current="orderPage"
            :page-size="10"
            show-total
            @on-change="loadOrders"
            style="margin-top: 16px; text-align: right;"
          />
        </Card>

        <!-- 操作按钮 -->
        <div class="action-buttons">
          <Button type="primary" @click="showTagModal">管理标签</Button>
        </div>
      </div>

      <div v-else>
        <Empty description="用户不存在"></Empty>
      </div>
    </Card>

    <!-- 管理标签弹窗 -->
    <Modal v-model="tagModal" title="管理用户标签" @on-ok="saveTags">
      <Form :model="tagForm">
        <FormItem label="用户标签">
          <Select v-model="tagForm.tags" multiple placeholder="选择标签">
            <Option v-for="tag in availableTags" :key="tag.value" :value="tag.value">
              {{ tag.label }}
            </Option>
          </Select>
        </FormItem>
      </Form>
    </Modal>

    <Modal v-model="adjustModal" title="储值调账" @on-ok="saveAdjust">
      <Form :label-width="110">
        <FormItem label="目标余额(元)">
          <InputNumber v-model="adjustForm.yuan" :min="0" :step="1" style="width: 200px" />
          <div style="color:#999;font-size:12px;margin-top:4px">将余额调整为该金额，可设为 0（清零）</div>
        </FormItem>
        <FormItem label="备注">
          <Input v-model="adjustForm.remark" type="textarea" :rows="2" />
        </FormItem>
      </Form>
    </Modal>
  </Content>
</template>

<script>
import { getCustomerDetail, getCustomerOrders, getCustomerStats, getCustomerBenefitCards, updateCustomerTags, getCustomerTags, updateCustomerMerchantFlag } from '@/api/customer'
import { storedValueLedgers, storedValueAdjust } from '@/api/storedValue'

export default {
  name: 'CustomerDetail',
  data() {
    return {
      loading: true,
      userInfo: null,
      stats: {},
      orders: [],
      orderTotal: 0,
      orderPage: 1,
      ledgers: [],
      ledgerTotal: 0,
      ledgerPage: 1,
      benefitCards: [],
      tagModal: false,
      adjustModal: false,
      adjustForm: { yuan: 0, remark: '' },
      tagForm: {
        tags: []
      },
      merchantSwitch: false,
      merchantSaving: false,
      availableTags: [
        { value: 'VIP', label: 'VIP用户' },
        { value: 'active', label: '活跃用户' },
        { value: 'new', label: '新用户' },
        { value: 'dormant', label: '沉睡用户' },
        { value: 'highValue', label: '高价值用户' }
      ],
      ledgerColumns: [
        {
          title: '时间',
          key: 'created_at',
          width: 170,
          render: (h, p) => h('span', this.formatTime(p.row.created_at)),
        },
        {
          title: '类型',
          key: 'biz_type',
          width: 90,
          render: (h, p) => {
            const map = { recharge: '充值', consume: '消费', refund: '退款', adjust: '调账' }
            return h('span', map[p.row.biz_type] || p.row.biz_type)
          },
        },
        {
          title: '变动',
          key: 'delta_fen',
          width: 110,
          render: (h, p) => {
            const y = (Number(p.row.delta_fen) || 0) / 100
            const color = y >= 0 ? '#19be6b' : '#ed4014'
            return h('span', { style: { color } }, `${y >= 0 ? '+' : ''}¥${y.toFixed(2)}`)
          },
        },
        {
          title: '余额',
          key: 'balance_after_fen',
          width: 110,
          render: (h, p) => h('span', `¥${((p.row.balance_after_fen || 0) / 100).toFixed(2)}`),
        },
        { title: '备注', key: 'remark', minWidth: 160 },
      ],
      benefitCardColumns: [
        { title: '名称', key: 'name', minWidth: 120 },
        { title: '折扣', key: 'zhe_label', width: 80 },
        {
          title: '有效期',
          key: 'valid',
          minWidth: 280,
          render: (h, p) => {
            const row = p.row
            if (!row.valid_from && !row.valid_to) return h('span', '—')
            return h('span', `${this.formatTime(row.valid_from)} ~ ${this.formatTime(row.valid_to)}`)
          },
        },
        {
          title: '商品',
          key: 'food_scope',
          width: 100,
          render: (h, p) => {
            const row = p.row
            if (row.food_scope === 'include') {
              const n = (row.food_ids || []).length
              return h('span', n ? `指定 ${n}` : '指定商品')
            }
            if (row.food_scope === 'all') return h('span', '全部')
            return h('span', '—')
          },
        },
        {
          title: '状态',
          key: 'status_text',
          width: 90,
          render: (h, p) => {
            const row = p.row
            const colorMap = { active: 'success', pending: 'warning', expired: 'default', off: 'error', deleted: 'error' }
            return h('Tag', { props: { color: colorMap[row.card_status] || 'default' } }, row.status_text || '—')
          },
        },
        {
          title: '领取时间',
          key: 'claimed_at',
          width: 170,
          render: (h, p) => h('span', this.formatTime(p.row.claimed_at)),
        },
      ],
      orderColumns: [
        { title: '订单ID', key: '_id', width: 100, render: (h, p) => h('span', p.row._id.toString().slice(-8)) },
        { title: '订单状态', slot: 'status', width: 100 },
        { title: '订单金额', key: 'total_price', width: 120, render: (h, p) => h('span', { style: { color: '#f56c6c' } }, `¥${(p.row.total_price || 0).toFixed(2)}`) },
        { title: '创建时间', key: 'create_time', width: 180, render: (h, p) => h('span', this.formatTime(p.row.create_time)) },
        { title: '操作', slot: 'action', width: 100, align: 'center' }
      ]
    }
  },
  computed: {
    displayLastLoginTime() {
      const u = this.userInfo || {}
      return u.lastLoginTime || u.last_login_time || u.member_since || u.create_time || null
    },
    displayLoginCount() {
      const u = this.userInfo || {}
      const n = u.loginCount != null ? u.loginCount : u.login_count
      if (Number.isFinite(Number(n)) && Number(n) > 0) {
        return Math.round(Number(n))
      }
      // 历史用户未记录登录次数时，至少注册/开通过会员算 1 次
      if (u.create_time || u.member_since) return 1
      return 0
    },
    benefitCardSummary() {
      const list = this.benefitCards || []
      if (!list.length) return '暂无'
      const active = list.filter((c) => c.card_status === 'active').length
      if (active > 0) return `${list.length} 张（${active} 张有效）`
      return `${list.length} 张（均已失效）`
    },
  },
  methods: {
    openAdjust() {
      const cur = Math.round(Number((this.userInfo && this.userInfo.balance_fen) || 0) || 0) / 100
      this.adjustForm = { yuan: Number(cur.toFixed(2)), remark: '' }
      this.adjustModal = true
    },
    async saveAdjust() {
      const yuan = Number(this.adjustForm.yuan)
      if (!Number.isFinite(yuan) || yuan < 0) {
        this.$Message.warning('请输入有效的目标余额（可为 0）')
        return false
      }
      const targetFen = Math.round(Number(yuan.toFixed(2)) * 100)
      try {
        const res = await storedValueAdjust({
          customer_id: this.userInfo._id,
          target_fen: targetFen,
          remark: this.adjustForm.remark || '后台调账',
        })
        if (res.data && res.data.code === 200) {
          this.$Message.success('调账成功')
          if (res.data.data && res.data.data.balance_fen != null) {
            this.$set(this.userInfo, 'balance_fen', res.data.data.balance_fen)
          }
          this.loadLedgers(1)
        } else {
          this.$Message.error((res.data && res.data.msg) || '调账失败')
          return false
        }
      } catch (e) {
        this.$Message.error(e.message || '调账失败')
        return false
      }
    },
    async loadLedgers(page = 1) {
      if (!this.userInfo || !this.userInfo._id) return
      this.ledgerPage = page
      try {
        const res = await storedValueLedgers({
          user_id: this.userInfo._id,
          page,
          pageSize: 10,
        })
        if (res.data && res.data.code === 200 && res.data.data) {
          this.ledgers = res.data.data.list || []
          this.ledgerTotal = res.data.data.total || 0
          if (res.data.data.balance_fen != null) {
            this.$set(this.userInfo, 'balance_fen', res.data.data.balance_fen)
          }
        }
      } catch (e) {
        console.error(e)
      }
    },
    async loadUserDetail() {
      const userId = this.$route.params.id
      console.log('加载用户详情，用户ID:', userId)
      if (!userId) {
        this.$Message.error('用户ID不能为空')
        return
      }

      this.loading = true
      // 清空之前的数据，避免显示错误
      this.userInfo = null
      try {
        const res = await getCustomerDetail(userId)
        console.log('用户详情API响应:', res)
        console.log('请求的用户ID:', userId)
        console.log('用户详情数据:', res.data?.data)
        if (res.data && (res.data.success || res.data.code === 200)) {
          this.userInfo = res.data.data
          console.log('当前用户信息:', this.userInfo)
          console.log('返回的用户ID:', this.userInfo?._id)
          this.tagForm.tags = this.userInfo.tags || []
          this.merchantSwitch = !!(this.userInfo && this.userInfo.is_merchant)
          this.loadLedgers(1)
          this.loadBenefitCards()
        } else {
          const errorMsg = res.data?.msg || res.data?.message || '获取用户详情失败'
          this.$Message.error(errorMsg)
          console.error('获取用户详情失败:', res.data)
        }
      } catch (error) {
        this.$Message.error('获取用户详情失败: ' + (error.message || error))
        console.error('获取用户详情异常:', error)
      } finally {
        this.loading = false
      }
    },
    async loadBenefitCards() {
      const userId = this.$route.params.id
      if (!userId) return
      try {
        const res = await getCustomerBenefitCards(userId)
        if (res.data && (res.data.success || res.data.code === 200)) {
          this.benefitCards = (res.data.data && res.data.data.list) || []
        } else {
          this.benefitCards = []
        }
      } catch (e) {
        console.error(e)
        this.benefitCards = []
      }
    },
    async loadUserStats() {
      const userId = this.$route.params.id
      try {
        const res = await getCustomerStats(userId)
        console.log('用户统计API响应:', res)
        if (res.data && (res.data.success || res.data.code === 200)) {
          this.stats = res.data.data || {}
        } else {
          console.error('获取用户统计失败:', res.data)
        }
      } catch (error) {
        console.error('获取用户统计异常:', error)
      }
    },
    async loadOrders(page = 1) {
      this.orderPage = page
      const userId = this.$route.params.id
      try {
        const res = await getCustomerOrders({ userId, page, pageSize: 10 })
        console.log('用户订单API响应:', res)
        if (res.data && (res.data.success || res.data.code === 200)) {
          this.orders = res.data.data?.list || []
          this.orderTotal = res.data.data?.total || 0
        } else {
          console.error('获取订单历史失败:', res.data)
        }
      } catch (error) {
        console.error('获取订单历史异常:', error)
      }
    },
    getUserStatusText(status) {
      // 与用户列表页面的状态判断逻辑保持一致
      const statusValue = status || 'active'
      return statusValue === 'active' || statusValue === '正常' || statusValue === 1 ? '正常' : '禁用'
    },
    getUserStatusColor(status) {
      // 与用户列表页面的状态判断逻辑保持一致
      const statusValue = status || 'active'
      return (statusValue === 'active' || statusValue === '正常' || statusValue === 1) ? 'success' : 'error'
    },
    showTagModal() {
      this.tagForm.tags = this.userInfo.tags || []
      this.tagModal = true
    },
    async handleMerchantSwitch(val) {
      if (!this.userInfo || !this.userInfo._id) return
      this.merchantSaving = true
      const prev = !val
      try {
        const res = await updateCustomerMerchantFlag({
          userId: this.userInfo._id,
          is_merchant: val,
        })
        if (res.data && (res.data.success || res.data.code === 200)) {
          this.$Message.success(val ? '已设为店员' : '已取消店员')
          this.userInfo.is_merchant = val
        } else {
          throw new Error(res.data?.msg || res.data?.message || '更新失败')
        }
      } catch (e) {
        this.merchantSwitch = prev
        this.$Message.error(typeof e === 'string' ? e : e.message || '更新失败')
      } finally {
        this.merchantSaving = false
      }
    },
    async saveTags() {
      try {
        const res = await updateCustomerTags({
          userId: this.userInfo._id,
          tags: this.tagForm.tags
        })
        
        if (res.data && (res.data.success || res.data.code === 200)) {
          this.$Message.success('标签更新成功')
          this.userInfo.tags = this.tagForm.tags
        } else {
          this.$Message.error(res.data?.message || '更新失败')
        }
      } catch (error) {
        this.$Message.error('更新失败')
      }
    },
    viewOrder(orderId) {
      this.$router.push({
        name: 'order-detail',
        params: { id: orderId }
      })
    },
    goBack() {
      this.$router.go(-1)
    },
    formatTime(time) {
      if (!time) return '-'
      return new Date(time).toLocaleString('zh-CN')
    },
    getStatusName(status) {
      const statusMap = {
        0: '待分配', 1: '已分配', 2: '前往中', 3: '已到达',
        4: '使用中', 5: '归还中', 6: '已完成', 7: '已取消', 8: '故障'
      }
      return statusMap[status] || '未知'
    },
    getStatusColor(status) {
      const colorMap = {
        0: 'default', 1: 'primary', 2: 'success', 3: 'warning',
        4: 'error', 5: 'default', 6: 'success', 7: 'error', 8: 'error'
      }
      return colorMap[status] || 'default'
    }
  },
  watch: {
    // 监听路由参数变化，当从用户A切换到用户B时重新加载数据
    '$route.params.id'(newId, oldId) {
      if (newId && newId !== oldId) {
        console.log('路由参数变化，从', oldId, '切换到', newId)
        this.loadUserDetail()
        this.loadUserStats()
        this.loadOrders()
        this.benefitCards = []
      }
    }
  },
  mounted() {
    this.loadUserDetail()
    this.loadUserStats()
    this.loadOrders()
  }
}
</script>

<style lang="less" scoped>
.customer-detail {
  .user-avatar {
    text-align: center;
    padding: 8px 0 16px;
  }

  .info-item {
    display: flex;
    align-items: flex-start;
    padding: 8px 0;
    line-height: 22px;
    min-height: 38px;

    .label {
      color: #909399;
      width: 80px;
      flex-shrink: 0;
    }

    .value {
      color: #303133;
      flex: 1;
      word-break: break-all;
    }

    .value--inline {
      display: inline-flex;
      align-items: center;
      flex: 0 1 auto;
      word-break: normal;
    }

    .adjust-btn {
      margin-left: 8px;
      flex-shrink: 0;
    }

    .id-text {
      font-family: Consolas, Monaco, monospace;
      font-size: 12px;
    }
  }

  .merchant-row {
    .row-inline {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: 8px;
      flex: 1;
    }
    .hint {
      font-size: 12px;
      color: #909399;
      max-width: 320px;
      line-height: 1.5;
    }
  }
  
  .stats-cards {
    .stat-card {
      background: #f5f7fa;
      border-radius: 8px;
      padding: 24px;
      text-align: center;
      
      .stat-value {
        font-size: 28px;
        font-weight: bold;
        color: #409EFF;
      }
      
      .stat-label {
        font-size: 14px;
        color: #909399;
        margin-top: 8px;
      }
    }
  }
  
  .action-buttons {
    margin-top: 16px;
    display: flex;
    gap: 12px;
  }

  .empty-hint {
    padding: 12px 0;
    color: #909399;
    font-size: 13px;
    text-align: center;
  }
}
</style>

